import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/EditRounded';

import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useToggle } from 'src/hooks/use-toggle';
import { FlexColumn } from 'src/components/flex';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal/CreateOrUpdateTourModal';
import TourDetailPopover from 'src/pages/landlord/tours/components/calendar/TourDetailPopover';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { getTimezoneAbbr, parseDateTimeInUserTimezone } from 'src/utils/format-date-time';

import styles from 'src/components/lead-sidebar/LeadSidebarTour.module.css';

interface Props {
    lead: ILead;
    showing: IShowing;
    color: string;
}

export default function LeadSidebarTour({
    lead,
    showing,
    color,
}: Props) {
    const [isEditing, { setTrue: startEditing, setFalse: stopEditing }] = useToggle();
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
    const { userSettings } = useAuthenticatedState();

    const timezoneOverride = userSettings.timezone_override;
    const showingDate = parseDateTimeInUserTimezone(showing.start_time, timezoneOverride);
    const tzAbbr = timezoneOverride ? getTimezoneAbbr(timezoneOverride) : null;

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        setPopoverAnchor(e.currentTarget);
    };

    return (
        <>
            <Box
                className={styles.showing}
                sx={{
                    borderLeft: '4px solid',
                    borderColor: color,
                    cursor: 'pointer',
                    '& svg': { display: 'none', fontSize: 20 },
                    '&:hover svg': { display: 'block' },
                }}
                onClick={handleClick}
                data-testid="lead-sidebar-tour-card"
            >
                <FlexColumn p={1} rowGap={0.5}>
                    <Typography variant="body2" className="ellipsisText" maxWidth={200}>
                        {lead.name}
                    </Typography>

                    <Typography variant="body2">
                        {showingDate.format('ddd, MMM D, YYYY')} at {showingDate.format('h:mm A')}
                        {tzAbbr && (
                            <Typography component="span" variant="body2" color="text.secondary" ml={0.5}>
                                {tzAbbr}
                            </Typography>
                        )}
                    </Typography>
                </FlexColumn>

                <EditIcon color="action" sx={{ position: 'absolute', right: 8, top: 8 }} />
            </Box>

            <TourDetailPopover
                showing={popoverAnchor ? showing : null}
                anchorEl={popoverAnchor}
                onClose={() => setPopoverAnchor(null)}
                onEdit={() => {
                    setPopoverAnchor(null);
                    startEditing();
                }}
            />

            {!!isEditing && (
                <CreateOrUpdateTourModal
                    open={isEditing}
                    onClose={stopEditing}
                    leads={[lead]}
                    selectedLeadUuid={showing.lead_uuid}
                    landlordUserId={showing.landlord_user_id}
                    initDateTime={showing.start_time}
                    initPropertyId={showing.property_id}
                    showingId={showing.id}
                    sourceMessageUuid={showing.source_message_uuid || undefined}
                />
            )}
        </>
    );
}
